document.addEventListener("DOMContentLoaded", () => {
  const app = new CancellationEstimatorPage();
  app.init();
});

class CancellationEstimatorPage {
  constructor() {
    this.data = null;
    this.elements = {
      loading: document.getElementById("ce-loading"),
      error: document.getElementById("ce-error"),
      content: document.getElementById("ce-content"),
      summary: document.getElementById("ce-summary"),
      saleDays: document.getElementById("ce-sale-days"),
      cases: document.getElementById("ce-cases"),
      empty: document.getElementById("ce-empty"),
      generated: document.getElementById("ce-generated"),
      dateFilter: document.getElementById("ce-date-filter"),
      riskFilter: document.getElementById("ce-risk-filter"),
      refresh: document.getElementById("ce-refresh")
    };
  }

  init() {
    if (!this.elements.loading || !this.elements.content) return;
    this.elements.refresh?.addEventListener("click", () => this.load(false));
    this.elements.dateFilter?.addEventListener("change", () => this.renderCases());
    this.elements.riskFilter?.addEventListener("change", () => this.renderCases());
    this.elements.cases?.addEventListener("click", event => {
      const button = event.target.closest("[data-evidence-toggle]");
      if (!button) return;
      const row = document.getElementById(button.dataset.evidenceToggle);
      if (!row) return;
      const opening = row.hidden;
      row.hidden = !opening;
      button.setAttribute("aria-expanded", String(opening));
      button.textContent = opening ? "Hide" : "Evidence";
    });
    this.load(false);
  }

  async load(saveSnapshot) {
    this.setLoading(true);
    try {
      const baseUrl = window.GoProvaloCancellationEstimator?.apiUrl;
      if (!baseUrl) throw new Error("Estimator API URL is not configured.");

      const url = new URL(baseUrl);
      url.searchParams.set("snapshot", saveSnapshot ? "1" : "0");
      url.searchParams.set("_", Date.now().toString());

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        cache: "no-store"
      });
      const text = await response.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error("The estimator returned an invalid response.");
      }
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Unable to load the estimator.");
      }

      this.data = payload;
      this.populateDateFilter();
      this.render();
      this.elements.content.hidden = false;
    } catch (error) {
      this.elements.error.textContent = error.message;
      this.elements.error.hidden = false;
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    this.elements.loading.hidden = !isLoading;
    this.elements.refresh.disabled = isLoading;
    if (isLoading) this.elements.error.hidden = true;
  }

  render() {
    this.renderSummary();
    this.renderSaleDays();
    this.renderCases();
    this.elements.generated.textContent =
      `Generated ${this.formatDateTime(this.data.generated_at)} · Rules ${this.data.rules_version}`;
  }

  renderSummary() {
    const summary = this.data.summary;

    const cards = [
      [
        "Expected non-sale rate",
        this.percent(summary.expected_non_sale_rate),
        "primary"
      ],
      [
        "Docket coverage",
        `${this.number(summary.analyzed_cases)} of ` +
        `${this.number(summary.scheduled_cases)} ` +
        `(${this.percent(summary.coverage_rate)})`,
        ""
      ],
      [
        "High-risk cases",
        this.number(summary.high_risk_cases),
        "warning"
      ],
      [
        "Missing dockets",
        this.number(summary.missing_docket_cases),
        ""
      ]
    ];

    this.elements.summary.innerHTML = cards
      .map(([label, value, tone]) => `
        <article class="ce-stat ${tone ? `ce-stat--${tone}` : ""}">
          <span>${this.escape(label)}</span>
          <strong>${this.escape(value)}</strong>
        </article>
      `)
      .join("");
  }

  renderSaleDays() {
  this.elements.saleDays.innerHTML = this.data.sale_days
    .map(day => {
      const barWidth = Math.min(
        100,
        Number(day.expected_non_sale_rate || 0)
      );

      return `
        <button
          class="ce-day"
          type="button"
          data-sale-date="${this.escape(day.sale_date)}"
        >
          <span>${this.formatDate(day.sale_date)}</span>

          <strong>
            ${this.percent(day.expected_non_sale_rate)}
          </strong>

          <small>
            ${this.number(day.high_risk_cases)} high risk ·
            ${this.number(day.analyzed_cases)} of
            ${this.number(day.scheduled_cases)} analyzed
          </small>

          <i>
            <b style="width:${barWidth}%"></b>
          </i>
        </button>
      `;
    })
    .join("");

  this.elements.saleDays
    .querySelectorAll("[data-sale-date]")
    .forEach(button => {
      button.addEventListener("click", () => {
        this.elements.dateFilter.value = button.dataset.saleDate;
        this.renderCases();

        document
          .querySelector(".ce-table-wrap")
          ?.scrollIntoView({ behavior: "smooth" });
      });
    });
}

  populateDateFilter() {
    const selected = this.elements.dateFilter.value;
    const dates = [...new Set(this.data.cases.map(item => item.sale_date))];
    this.elements.dateFilter.innerHTML =
      `<option value="">All dates</option>` +
      dates.map(date => `<option value="${this.escape(date)}">${this.formatDate(date)}</option>`).join("");
    if (dates.includes(selected)) this.elements.dateFilter.value = selected;
  }

  renderCases() {
    if (!this.data) return;
    const date = this.elements.dateFilter.value;
    const risk = this.elements.riskFilter.value;
    const cases = this.data.cases.filter(item =>
      (!date || item.sale_date === date) &&
      (!risk || item.risk_level === risk)
    );

    this.elements.empty.hidden = cases.length > 0;
    this.elements.cases.innerHTML = cases.map((item, index) => {
      const evidenceId = `ce-evidence-${index}`;
      return `
        <tr>
          <td data-label="Sale date">${this.formatDate(item.sale_date)}</td>
          <td data-label="Case / property">
            <strong>${this.escape(item.case_number)}</strong>
            <span>${this.escape([item.address, item.city, item.zip].filter(Boolean).join(", ") || "Address unavailable")}</span>
          </td>
          <td data-label="Risk">
            <span class="ce-risk ce-risk--${this.slug(item.risk_level)}">${this.number(item.risk_score)}%</span>
            <small>${this.escape(item.risk_level)}</small>
          </td>
          <td data-label="Estimated outcome">
            <strong>${this.escape(item.estimated_outcome)}</strong>
            ${item.requires_review ? `<span class="ce-review">Review required</span>` : ""}
          </td>
          <td data-label="Primary evidence">
            <span>${this.escape(item.primary_reason)}</span>
            ${item.trigger_docket_entry ? `<small>${this.escape(item.trigger_docket_entry)}${item.trigger_din ? ` · DIN ${this.escape(item.trigger_din)}` : ""}</small>` : ""}
          </td>
          <td data-label="Updated">${this.formatDateTime(item.source_last_update)}</td>
          <td>
            <button class="ce-link" type="button" data-evidence-toggle="${evidenceId}" aria-expanded="false">Evidence</button>
          </td>
        </tr>
        <tr id="${evidenceId}" class="ce-evidence-row" hidden>
          <td colspan="7">${this.renderEvidence(item)}</td>
        </tr>
      `;
    }).join("");
  }

  renderEvidence(item) {
    if (!item.evidence?.length) {
      return `<div class="ce-evidence"><p>No cancellation evidence detected after the latest Notice of Sale.</p></div>`;
    }
    return `
      <div class="ce-evidence">
        <h3>Detected docket evidence</h3>
        ${item.evidence.map(evidence => `
          <article>
            <div>
              <strong>${this.escape(evidence.description)}</strong>
              <span>${this.formatDate(evidence.docket_date)}${evidence.din ? ` · DIN ${this.escape(evidence.din)}` : ""}</span>
            </div>
            <p>${this.escape(evidence.docket_entry || "Docket entry")}${evidence.comments ? ` — ${this.escape(evidence.comments)}` : ""}</p>
          </article>
        `).join("")}
      </div>
    `;
  }

  formatDate(value) {
    if (!value) return "N/A";
    const date = new Date(`${value}T12:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-US", {
      month: "short", day: "numeric", year: "numeric"
    }).format(date);
  }

  formatDateTime(value) {
    if (!value) return "N/A";
    const normalized = String(value).includes("T") ? value : `${value.replace(" ", "T")}Z`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit"
    }).format(date);
  }

  number(value) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(Number(value || 0));
  }

  percent(value) {
    if (value === null || value === undefined) {
      return "N/A";
    }

    return `${this.number(value)}%`;
  }

  slug(value) {
    return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  }

  escape(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }
}

